// Short-code ↔ canonical role-name translation for the `?as=<code>` deep-link
// contract. Short codes are UI-internal only (LLD §4.7, §7.3, DESIGN.md §9);
// the backend rejects anything that is not a full canonical role name.
//
// Rule of thumb for the map: first letter of each underscore-separated word.
// Collisions (e.g. `site_manager` vs `site_investigator`) are resolved by
// listing both full names explicitly — we never guess.
//
// Adding a new role:
//   1. Extend ROLE_SHORT_CODES below (both directions are derived from this
//      one table).
//   2. Update DESIGN.md §9 if the role is user-visible.
//   3. If the backend's KNOWN_ROLES changes, update RoleConstants.java too.

const ROLE_SHORT_CODES: Readonly<Record<string, string>> = Object.freeze({
  sm: "site_manager",
  si: "site_investigator",
  sv: "site_viewer",
  ra: "regional_admin",
  fa: "fleet_admin",
  sa: "system_auditor",
  op: "operator",
});

/**
 * Translate a `?as=<value>` query-param value into a canonical role name.
 *
 * Accepts either:
 *   - A short code (`sm`, `fa`, …) — translated via the table above.
 *   - A canonical full role name (`site_manager`, `fleet_admin`, …) —
 *     passed through verbatim (case-insensitive).
 *
 * Returns `null` if the value does not map to any known canonical name.
 * Callers MUST additionally verify the returned role is in the user's
 * `distinct_roles` before setting `sessionStorage.currentRole` (DESIGN.md
 * §9: "never pick something the user doesn't hold").
 */
export function translateRoleParam(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim().toLowerCase();
  if (trimmed.length === 0) return null;

  // Short code hit.
  const mapped = ROLE_SHORT_CODES[trimmed];
  if (mapped !== undefined) return mapped;

  // Full canonical name: must match an entry in the values of the table.
  // We intentionally do NOT accept arbitrary strings — that would mask typos
  // as "role the user doesn't hold" rather than "unknown role".
  const canonicalValues = Object.values(ROLE_SHORT_CODES);
  if (canonicalValues.includes(trimmed)) return trimmed;

  return null;
}

/** Exposed for tests / docs — callers should NOT mutate. */
export const ROLE_SHORT_CODE_TABLE = ROLE_SHORT_CODES;
